<script setup>
import Multiselect from '@vueform/multiselect';
import {computed, getCurrentInstance} from "vue";

const model = defineModel({
  type: [Array, String, Number],
});

defineProps({
  placeholder: {
    type: String,
    required: false,
    default: 'Select'
  },
  mode: {
    type: String,
    required: false,
    default: 'single'
  },
  closeOnSelect: {
    type: Boolean,
    default: false
  },
  options: {
    type: [Array, Object, Function],
    required: true
  },
  validationErrors: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  max: {
    type: Number,
    required: false,
    default: -1
  },
  searchable: {
    type: Boolean,
    required: false,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  groups: {
    type: Boolean,
    default: false,
  },
  canDeselect: {
    type: Boolean,
    default: true,
  },
  caret: {
    type: Boolean,
    required: false,
    default: true,
  },
  noOptionsText: {
    type: String,
    required: false,
    default: 'The list is empty',
  },
  noResultsText: {
    type: String,
    required: false,
    default: 'No results found',
  },
  minChars: {
    type: Number,
    required: false,
    default: 0,
  },
  resolveOnLoad: {
    type: Boolean,
    required: false,
    default: true,
  },
  filterResults: {
    type: Boolean,
    required: false,
    default: true,
  },
  delay: {
    type: Number,
    required: false,
    default: -1,
  },
  classes: {
    type: Object,
    required: false,
    default: () => {},
  },
  canClear: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasError: {
    type: Boolean,
    required: false,
  }
});

const emit = defineEmits(['searchChange']);

const { slots } = getCurrentInstance();

const hasIconSlot = computed(() => {
  return !!slots.icon;
});
</script>

<template>
  <div>
    <multiselect
      v-model="model"
      :can-clear="canClear"
      :caret="caret"
      :close-on-select="closeOnSelect"
      :max="max"
      :mode="mode"
      :groups="groups"
      :options="options"
      :placeholder="placeholder"
      :searchable="true"
      :disabled="disabled"
      :can-deselect="canDeselect"
      :no-options-text="noOptionsText"
      :no-results-text="noResultsText"
      :min-chars="minChars"
      :resolve-on-load="resolveOnLoad"
      :filter-results="filterResults"
      :delay="delay"
      :classes="{
        container: `tw-relative tw-mx-auto tw-w-full tw-h-full tw-flex tw-items-center ${Object.keys(validationErrors).length > 0 || hasError ? '!tw-border-danger': 'tw-border-neutral'} tw-justify-end tw-box-border tw-cursor-pointer tw-border tw-border-neutral focus-within:tw-border-primary tw-rounded-lg tw-bg-white tw-text-base tw-leading-snug tw-outline-none tw-select-none`,
        wrapper: `tw-flex tw-w-full tw-cursor-pointer tw-pr-6 tw-py-3 tw-items-center ${Object.keys(validationErrors).length > 0 || hasError ? '!tw-border-danger': 'tw-border-neutral-900'} ${hasIconSlot ? 'tw-pl-14': 'tw-pl-6'}`,
        singleLabel: `tw-flex tw-items-center tw-h-full tw-absolute tw-left-0 tw-top-0 tw-pointer-events-none tw-bg-transparent tw-leading-snug tw-text-neutral-900 ${hasIconSlot ? 'tw-pl-14': 'tw-pl-6'}`,
        search: 'tw-text-neutral-900 tw-leading-5 tw-text-base tw-inset-0 tw-border-0 tw-outline-none tw-focus:ring-0 tw-appearance-none tw-w-full tw-bg-transparent',
        placeholder: `tw-flex tw-items-center tw-h-full tw-absolute tw-left-0 tw-top-0 tw-pointer-events-none tw-bg-transparent tw-leading-5 tw-text-slate-300 ${hasIconSlot ? 'tw-pl-14' : 'tw-pl-6'} tw-select-none`,
        option: `hover:tw-bg-primary-100 hover:tw-text-neutral-900 tw-flex tw-items-center tw-justify-start tw-box-border tw-text-left tw-cursor-pointer tw-text-base tw-leading-snug tw-py-2 tw-px-3 tw-rounded`,
        options: 'tw-flex tw-flex-col tw-m-0 tw-gap-2 tw-list-none',
        optionSelected: 'tw-text-white tw-bg-primary',
        dropdown: 'tw-max-h-96 tw-absolute -tw-left-px -tw-right-px -tw-bottom-4 tw-transform tw-translate-y-full tw-border tw-border-primary -tw-mt-px tw-overflow-y-scroll tw-z-50 tw-p-4 tw-bg-white tw-flex tw-flex-col tw-rounded-lg',
        dropdownTop: '-tw-translate-y-full tw-top-px tw-bottom-auto tw-rounded-b-none tw-rounded-t-lg',
        dropdownHidden: 'tw-hidden',
        groupLabel:'tw-inline-flex tw-items-center tw-rounded-xl tw-font-bold tw-text-neutral-900 tw-leading-[1.125rem] tw-uppercase tw-py-2.5 tw-px-4 tw-w-full tw-gap-x-4 tw-justify-between',
        groupLabelPointable: 'tw-pointer-events-none',
        groupLabelPointed: 'tw-bg-primary-100',
        groupLabelSelected: 'tw-bg-primary tw-text-white',
        groupLabelSelectedPointed: 'tw-bg-primary tw-text-white',
        groupOptions: 'tw-m-0 tw-px-4',
        caretOpen: 'tw-pointer-events-auto',
        clear: 'tw-flex',
        ...classes,
      }"
      @search-change="(query) => emit('searchChange', query)"
    >
      <template #caret="{ isOpen, handleCaretClick }">
        <svg-vue
          class="tw-w-6 tw-h-6 tw-text-neutral-900 -tw-mr-3 tw-transition-transform tw-duration-300 tw-z-10 tw-ml-auto"
          :class="{
            'tw-rotate-180': isOpen,
            'tw-pointer-events-none' : !isOpen
          }"
          icon="icons/dropdown"
          @click="handleCaretClick"
        />
      </template>
    </multiselect>

    <!-- IMPORTANT! Add tw-relative on input when using the slot -->
    <div class="tw-absolute tw-left-6 tw-top-1/2 tw-transform tw--translate-y-1/2">
      <slot name="icon" />
    </div>

    <small
      v-for="(error, index) in validationErrors"
      :key="index"
      class="tw-text-danger tw-self-baseline tw-absolute tw-mt-1"
    >{{ error }}</small>
  </div>
</template>
